import { StrictMode, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from './theme/ThemeProvider';
import { IS_CLIENT_BUILD } from './buildTarget';
import { runDeckAudit } from './decks/audit';
import './styles/globals.css';

/**
 * Pick the right top-level surface for this build target. We use dynamic
 * imports gated on IS_CLIENT_BUILD so Vite can statically eliminate the
 * unused branch — internal builds never include ClientApp, and (more
 * importantly) client builds never include App.tsx, which transitively
 * imports the full internal deck registry.
 *
 * This is the load-bearing line of the security boundary: without it,
 * a client build would include code for every internal deck even though
 * the runtime never renders them.
 */
async function loadSurface(): Promise<ComponentType> {
  if (IS_CLIENT_BUILD) {
    const mod = await import('./ClientApp');
    return mod.default;
  }
  const mod = await import('./App');
  return mod.default;
}

async function bootstrap() {
  // Run the audit before mounting. If it throws, neither App nor
  // ClientApp will render — the page stays empty and the error surfaces
  // in the dev server / browser console.
  runDeckAudit();

  const Surface = await loadSurface();

  const root = document.getElementById('root');
  if (!root) throw new Error('Could not find #root in index.html');

  createRoot(root).render(
    <StrictMode>
      <ThemeProvider>
        <Surface />
      </ThemeProvider>
    </StrictMode>
  );
}

bootstrap();
